/**
 * CPH Wheels — Tire Finder
 * Samme flow som wheel-finder, men for dæk:
 * 1. Plate/manuel → henter bil-data fra Wheel-Size
 * 2. Parser godkendte dækstørrelser (tire_sizes)
 * 3. Søger Storefront API efter dæk der matcher
 * 4. Filtrerer klient-side og viser resultater
 */
(function () {
  'use strict';

  const PROXY_BASE = window.__tfConfig?.proxyBaseUrl || '';
  const STOREFRONT_TOKEN = window.__tfConfig?.storefrontToken || '';
  // Understøt flere kollektioner (komma-separeret) eller én
  const COLLECTION_HANDLES = (window.__tfConfig?.collectionHandle || 'sommerdaek,vinterdaek,helarsdaek')
    .split(',').map(s => s.trim()).filter(Boolean);

  let selectedMake = null;
  let selectedModel = null;
  let selectedYear = null;
  let carTires = null; // { sizes: [{ width, profile, diameter }], ... }
  let allProducts = [];
  let matchedProducts = [];
  let activeFilters = {};
  let activeSort = 'relevance';
  const PER_PAGE = 50;
  let visibleCount = PER_PAGE;
  let filteredCache = [];

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const container = $('#tire-finder');
  if (!container) return;

  const plateInput = $('#tf-plate', container);
  const plateBtn = $('#tf-plate-btn', container);
  const plateInfo = $('#tf-plate-info', container);
  const makeEl = $('#tf-make', container);
  const modelEl = $('#tf-model', container);
  const yearEl = $('#tf-year', container);
  const searchBtn = $('#tf-search-btn', container);
  const msgEl = $('#tf-message', container);
  const carSummary = $('#tf-car-summary', container);
  const loadingEl = $('#tf-loading', container);
  const resultsSection = $('#tf-results', container);
  const resultsGrid = $('#tf-results-grid', container);
  const resultsCount = $('#tf-results-count', container);
  const sortSelect = $('#tf-sort-select', container);
  const loadMoreBtn = $('#tf-load-more', container);
  const filtersEl = $('#tf-filters', container);

  /* ─── API helpers ─── */
  function wsApi(path, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${PROXY_BASE}${path}${qs ? '?' + qs : ''}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!json) return [];
        return Array.isArray(json) ? json : (json.data || json);
      });
  }

  function storefrontQuery(query, variables) {
    return fetch(`/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());
  }

  /* ─── Populate dropdowns ─── */
  async function populateMakes() {
    const data = await wsApi('/makes/');
    makeEl.innerHTML = '<option value="">Mærke</option>';
    (data || [])
      .sort((a, b) => a.name.localeCompare(b.name, 'da'))
      .forEach((m) => {
        const opt = document.createElement('option');
        opt.value = m.slug;
        opt.textContent = m.name;
        makeEl.appendChild(opt);
      });
  }

  async function populateModels(makeSlug) {
    modelEl.innerHTML = '<option value="">Indlæser...</option>';
    modelEl.disabled = true;
    const data = await wsApi('/models/', { make: makeSlug });
    modelEl.innerHTML = '<option value="">Model</option>';
    (data || [])
      .sort((a, b) => a.name.localeCompare(b.name, 'da'))
      .forEach((m) => {
        const opt = document.createElement('option');
        opt.value = m.slug;
        opt.textContent = m.name;
        modelEl.appendChild(opt);
      });
    modelEl.disabled = false;
  }

  async function populateYears(makeSlug, modelSlug) {
    yearEl.innerHTML = '<option value="">Indlæser...</option>';
    yearEl.disabled = true;
    const data = await wsApi('/generations/', { make: makeSlug, model: modelSlug });
    yearEl.innerHTML = '<option value="">Årgang</option>';
    const entries = [];
    (data || []).forEach((gen) => {
      (gen.years || []).forEach((y) => {
        entries.push({ year: y, genName: gen.name || '' });
      });
    });
    entries
      .sort((a, b) => b.year - a.year)
      .forEach((e) => {
        const opt = document.createElement('option');
        opt.value = e.year;
        opt.textContent = e.genName ? `${e.year} (${e.genName})` : String(e.year);
        yearEl.appendChild(opt);
      });
    yearEl.disabled = false;
  }

  /* ─── Events ─── */
  makeEl.addEventListener('change', () => {
    selectedMake = makeEl.value;
    selectedModel = null;
    selectedYear = null;
    modelEl.innerHTML = '<option value="">Model</option>';
    modelEl.disabled = true;
    yearEl.innerHTML = '<option value="">Årgang</option>';
    yearEl.disabled = true;
    searchBtn.disabled = true;
    if (selectedMake) populateModels(selectedMake);
    updateURL();
  });

  modelEl.addEventListener('change', () => {
    selectedModel = modelEl.value;
    selectedYear = null;
    yearEl.innerHTML = '<option value="">Årgang</option>';
    yearEl.disabled = true;
    searchBtn.disabled = true;
    if (selectedModel) populateYears(selectedMake, selectedModel);
    updateURL();
  });

  yearEl.addEventListener('change', () => {
    selectedYear = yearEl.value;
    updateURL();
    // Auto-søg når år vælges
    if (selectedMake && selectedModel && selectedYear) {
      doSearch(selectedMake, selectedModel, selectedYear);
    }
  });

  plateInput.addEventListener('input', () => {
    plateInput.value = plateInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  });

  plateInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') plateBtn.click();
  });

  plateBtn.addEventListener('click', async () => {
    const plate = plateInput.value.trim();
    if (!plate || plate.length < 4) {
      plateInfo.textContent = 'Indtast venligst en gyldig nummerplade.';
      plateInfo.className = 'tf-plate-info tf-plate-info--error';
      return;
    }

    plateBtn.disabled = true;
    plateBtn.textContent = '...';
    plateInfo.textContent = 'Slår op...';
    plateInfo.className = 'tf-plate-info';

    try {
      const resp = await fetch(`${PROXY_BASE}/lookup-plate/?plate=${encodeURIComponent(plate)}`);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Opslag fejlede');
      }
      const data = await resp.json();
      if (!data.wheelsize || !data.wheelsize.make || !data.wheelsize.model || !data.wheelsize.year) {
        plateInfo.textContent = `Fandt bil (${data.vehicle?.make || ''} ${data.vehicle?.model || ''}), men kunne ikke matche. Prøv manuel søgning.`;
        plateInfo.className = 'tf-plate-info tf-plate-info--error';
        return;
      }
      const displayYear = data.vehicle.year && data.vehicle.year > 0 ? data.vehicle.year : data.wheelsize.year;
      plateInfo.textContent = `✓ Fandt: ${data.vehicle.make} ${data.vehicle.model} (${displayYear})`;
      plateInfo.className = 'tf-plate-info tf-plate-info--success';
      await doSearch(data.wheelsize.make, data.wheelsize.model, data.wheelsize.year);
    } catch (err) {
      console.error(err);
      plateInfo.textContent = `Kunne ikke finde bil med nummerplade "${plate}".`;
      plateInfo.className = 'tf-plate-info tf-plate-info--error';
    } finally {
      plateBtn.disabled = false;
      plateBtn.textContent = '→';
    }
  });

  sortSelect?.addEventListener('change', (e) => {
    activeSort = e.target.value;
    applyFiltersAndSort();
  });

  loadMoreBtn?.addEventListener('click', () => {
    visibleCount += PER_PAGE;
    renderProductCards(filteredCache);
  });

  /* ─── Main search flow ─── */
  async function doSearch(make, model, year) {
    showLoading(true);
    resultsSection.hidden = true;

    // Hent fitment-data
    const data = await wsApi('/search/by_model/', { make, model, year, region: 'eudm' });
    if (!data || !data.length) {
      showLoading(false);
      carSummary.hidden = true;
      showError('Ingen dæk-data fundet for denne bil.');
      return;
    }

    carTires = parseTireData(data, { make, model, year });

    // Hvis kunden kommer fra konfigurator med specifik fælg: filtrér sizes til kun dem der passer til fælgen
    if (rimFilter && rimFilter.diameter) {
      const etrtoRange = rimFilter.width && ETRTO[rimFilter.width] ? ETRTO[rimFilter.width] : null;
      const originalSizes = carTires.sizes;
      carTires.sizes = carTires.sizes.filter((s) => {
        if (s.diameter !== rimFilter.diameter) return false;
        if (etrtoRange && (s.width < etrtoRange[0] || s.width > etrtoRange[1])) return false;
        return true;
      });
      console.log(`[TireFinder] Rim filter: ${rimFilter.diameter}"`, rimFilter.width ? `${rimFilter.width}J` : '',
        `→ ${carTires.sizes.length} af ${originalSizes.length} godkendte sizes passer`);
    }

    showCarSummary(carTires);

    if (!carTires.sizes.length) {
      showLoading(false);
      showError(rimFilter
        ? `Ingen dækstørrelser matcher valgt fælg (${rimFilter.diameter}" ${rimFilter.width || ''}J) for denne bil.`
        : 'Ingen godkendte dækstørrelser fundet.');
      return;
    }

    // Søg Shopify for dæk
    await searchProducts();
  }

  function parseTireData(modifications, carInfo) {
    const sizesSet = new Set();
    const sizes = [];
    let makeName = carInfo.make;
    let modelName = carInfo.model;

    modifications.forEach((mod) => {
      if (mod.make?.name) makeName = mod.make.name;
      if (mod.generation?.name) modelName = mod.generation.name;

      (mod.wheels || []).forEach((ws) => {
        ['front', 'rear'].forEach((axle) => {
          const w = ws[axle];
          if (!w || !w.tire_width || !w.tire_aspect_ratio || !w.rim_diameter) return;
          const key = `${w.tire_width}/${w.tire_aspect_ratio}R${w.rim_diameter}`;
          if (sizesSet.has(key)) return;
          sizesSet.add(key);
          sizes.push({
            width: parseInt(w.tire_width, 10),
            profile: parseInt(w.tire_aspect_ratio, 10),
            diameter: parseInt(w.rim_diameter, 10),
            display: key,
          });
        });
      });
    });

    return { make: makeName, model: modelName, year: carInfo.year, sizes };
  }

  function showCarSummary(car) {
    const sizeList = car.sizes.map((s) => s.display).join(', ');
    carSummary.innerHTML = `
      <div class="tf-summary">
        <strong>${escapeHTML(car.make)} ${escapeHTML(car.model)} (${car.year})</strong>
        <span class="tf-summary__specs">Godkendte dækstørrelser: ${escapeHTML(sizeList)}</span>
      </div>
    `;
    carSummary.hidden = false;
  }

  function showError(msg) {
    resultsSection.hidden = false;
    resultsGrid.innerHTML = `<li class="tf-no-results"><p>${escapeHTML(msg)}</p></li>`;
    resultsCount.textContent = '';
    filtersEl.hidden = true;
  }

  /* ─── Storefront product search (uden metafields — scope blokeret) ─── */
  const PRODUCTS_QUERY = `
    query FindTires($handle: String!, $filters: [ProductFilter!], $first: Int!, $after: String) {
      collection(handle: $handle) {
        handle
        products(first: $first, after: $after, filters: $filters) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              title
              handle
              vendor
              availableForSale
              featuredImage { url altText }
              priceRange { minVariantPrice { amount currencyCode } }
            }
          }
        }
      }
    }
  `;

  async function searchProducts() {
    allProducts = [];
    const seenIds = new Set();

    // Kør queries parallelt: hver godkendt (bredde+profil+tommer) kombination × 3 kollektioner
    const queries = [];
    COLLECTION_HANDLES.forEach((handle) => {
      carTires.sizes.forEach((s) => {
        queries.push(fetchExactSize(handle, s));
      });
    });

    const results = await Promise.all(queries);

    results.flat().forEach((p) => {
      if (!seenIds.has(p.id)) {
        seenIds.add(p.id);
        allProducts.push(p);
      }
    });

    window.__tfDebug = { carTires, allProducts };
    showLoading(false);
    renderResults(allProducts);
  }

  // Sæson-mapping fra kollektion-handle
  const SEASON_FROM_HANDLE = {
    sommerdaek: 'Sommerdæk',
    vinterdaek: 'Vinterdæk',
    helarsdaek: 'Helårsdæk',
  };

  async function fetchExactSize(handle, size) {
    const collected = [];
    let cursor = null;
    let hasMore = true;

    while (hasMore) {
      const filters = [
        { productMetafield: { namespace: 'custom', key: 'daekbredde', value: String(size.width) } },
        { productMetafield: { namespace: 'custom', key: 'daekprofil', value: String(size.profile) } },
        { productMetafield: { namespace: 'custom', key: 'daektommer', value: String(size.diameter) } },
      ];

      try {
        const data = await storefrontQuery(PRODUCTS_QUERY, {
          handle: handle,
          filters,
          first: 50,
          after: cursor,
        });

        console.log(`[TF] ${handle} ${size.width}/${size.profile}R${size.diameter}:`,
          data?.errors ? 'ERRORS: ' + JSON.stringify(data.errors).substring(0, 200) :
          'edges=' + (data?.data?.collection?.products?.edges?.length || 0)
        );

        const collection = data?.data?.collection;
        if (!collection) break;

        const edges = collection.products.edges || [];
        const pageInfo = collection.products.pageInfo || {};

        edges.forEach((edge) => {
          const node = edge.node;
          collected.push({
            id: node.id,
            title: node.title,
            handle: node.handle,
            available: node.availableForSale,
            image: node.featuredImage?.url || '',
            imageAlt: node.featuredImage?.altText || node.title,
            price: node.priceRange.minVariantPrice.amount,
            currency: node.priceRange.minVariantPrice.currencyCode,
            width: size.width,
            profile: size.profile,
            diameter: size.diameter,
            sason: SEASON_FROM_HANDLE[handle] || '',
            fabrikant: node.vendor || '',
            daekmodel: '',
            braendstof: '',
            vejgreb: '',
            stoj: '',
            stoj_marke: '',
            load: '',
            hastighed: '',
            egenskaber: '',
          });
        });

        hasMore = pageInfo.hasNextPage || false;
        cursor = pageInfo.endCursor || null;
      } catch (e) {
        console.error('Storefront API fejl:', e);
        hasMore = false;
      }
    }

    return collected;
  }

  /* ─── Render ─── */
  function renderResults(products) {
    matchedProducts = products;
    activeFilters = {};
    visibleCount = PER_PAGE;

    // Auto-anvend season-filter fra URL (f.eks. efter konfigurator-flow)
    if (pendingSeasonFilter) {
      activeFilters['sason'] = [pendingSeasonFilter];
      pendingSeasonFilter = null;
    }

    if (products.length === 0) {
      showError('Ingen dæk fundet til din bil i butikken.');
      return;
    }

    buildFilterSidebar();
    applyFiltersAndSort();
    resultsSection.hidden = false;
  }

  // Filter-definitioner: hvilke metafelter skal vises som filtre
  const FILTER_DEFS = [
    { key: 'fabrikant', label: 'Producent', getter: (p) => p.fabrikant },
    { key: 'daekmodel', label: 'Model', getter: (p) => p.daekmodel },
    { key: 'sason', label: 'Sæson', getter: (p) => p.sason },
    { key: 'diameter', label: 'Diameter', getter: (p) => p.diameter, sort: 'num' },
    { key: 'width', label: 'Bredde', getter: (p) => p.width, sort: 'num' },
    { key: 'profile', label: 'Profil (Højde)', getter: (p) => p.profile, sort: 'num' },
    { key: 'braendstof', label: 'Brændstof forbrug', getter: (p) => p.braendstof },
    { key: 'vejgreb', label: 'Vejgreb', getter: (p) => p.vejgreb },
    { key: 'stoj', label: 'Støj niveau dB', getter: (p) => p.stoj, sort: 'num' },
    { key: 'stoj_marke', label: 'Støj mærke', getter: (p) => p.stoj_marke },
    { key: 'load', label: 'Load', getter: (p) => p.load, sort: 'num' },
    { key: 'hastighed', label: 'Hastighed', getter: (p) => p.hastighed },
    { key: 'egenskaber', label: 'Særlige egenskaber', getter: (p) => p.egenskaber },
    { key: 'availability', label: 'Tilgængelighed', getter: (p) => p.available ? 'På lager' : 'Udsolgt' },
    { key: 'price_range', label: 'Pris', getter: (p) => {
      const pr = parseFloat(p.price) || 0;
      if (pr < 500) return 'Under 500 kr';
      if (pr < 1000) return '500 - 1.000 kr';
      if (pr < 1500) return '1.000 - 1.500 kr';
      if (pr < 2000) return '1.500 - 2.000 kr';
      return 'Over 2.000 kr';
    }},
  ];

  const CARET_SVG = '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path></svg>';
  const CHECK_SVG = '<div class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-checkmark" viewBox="0 0 12 9"><path fill="currentColor" fill-rule="evenodd" d="M11.35.643a.5.5 0 0 1 .006.707l-6.77 6.886a.5.5 0 0 1-.719-.006L.638 4.845a.5.5 0 1 1 .724-.69l2.872 3.011 6.41-6.517a.5.5 0 0 1 .707-.006z" clip-rule="evenodd"></path></svg></div>';
  const CHECKBOX_SVG = '<svg width="16px" height="16px" viewBox="0 0 16 16"><rect width="16" height="16" fill="none" stroke="currentColor"></rect></svg>';

  function applyFilterSet(products, filters) {
    let result = products;
    Object.entries(filters).forEach(([key, values]) => {
      if (!values || !values.length) return;
      const def = FILTER_DEFS.find((d) => d.key === key);
      if (!def) return;
      result = result.filter((p) => {
        const v = def.getter(p);
        return values.includes(String(v));
      });
    });
    return result;
  }

  function buildFilterSidebar() {
    filtersEl.hidden = false;
    let html = '<div class="facets facets-vertical"><h2 class="facet-filters__label caption-large text-body" style="margin-bottom:1rem;">Filter:</h2>';

    FILTER_DEFS.forEach((def, idx) => {
      // Find tilgængelige værdier MED andre aktive filtre (men uden dette)
      const otherFilters = { ...activeFilters };
      delete otherFilters[def.key];
      const available = applyFilterSet(matchedProducts, otherFilters);

      const counts = new Map();
      available.forEach((p) => {
        const raw = def.getter(p);
        if (raw == null || raw === '') return;
        const v = String(raw);
        counts.set(v, (counts.get(v) || 0) + 1);
      });

      let entries = [...counts.entries()];
      if (entries.length === 0) return;

      if (def.sort === 'num') {
        entries.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
      } else {
        entries.sort((a, b) => a[0].localeCompare(b[0], 'da'));
      }

      const selectedCount = activeFilters[def.key]?.length || 0;
      const isOpen = selectedCount > 0 ? ' open' : '';

      const items = entries.map(([val, count], i) => {
        const checked = activeFilters[def.key]?.includes(val) ? ' checked' : '';
        const safeVal = escapeHTML(val);
        const id = `tf-filter-${def.key}-${i}`;
        return `<li class="list-menu__item facets__item">
          <label for="${id}" class="facets__label facet-checkbox">
            <input type="checkbox" data-filter-key="${def.key}" value="${safeVal}" id="${id}"${checked}>
            ${CHECKBOX_SVG}
            ${CHECK_SVG}
            <span class="facet-checkbox__text" aria-hidden="true">
              <span class="facet-checkbox__text-label">${safeVal}</span> (${count})
            </span>
          </label>
        </li>`;
      }).join('');

      html += `
        <details class="facets__disclosure-vertical js-filter" data-index="${idx + 1}"${isOpen}>
          <summary class="facets__summary caption-large focus-offset">
            <div>
              <span class="facets__summary-label">${escapeHTML(def.label)}
                <span class="facets__selected${selectedCount === 0 ? ' hidden' : ''}">(${selectedCount})</span>
              </span>
              ${CARET_SVG}
            </div>
          </summary>
          <div class="parent-display facets__display-vertical">
            <fieldset class="facets-wrap parent-wrap facets-wrap-vertical">
              <legend class="visually-hidden">${escapeHTML(def.label)}</legend>
              <ul class="facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled" role="list">
                ${items}
              </ul>
            </fieldset>
          </div>
        </details>
      `;
    });

    html += '</div>';
    filtersEl.innerHTML = html;
    $$('input[data-filter-key]', filtersEl).forEach((input) => {
      input.addEventListener('change', onFilterChange);
    });
  }

  function onFilterChange() {
    activeFilters = {};
    $$('input[data-filter-key]:checked', filtersEl).forEach((input) => {
      const k = input.getAttribute('data-filter-key');
      if (!activeFilters[k]) activeFilters[k] = [];
      activeFilters[k].push(input.value);
    });
    applyFiltersAndSort();
    buildFilterSidebar();
  }

  function applyFiltersAndSort() {
    // Klient-side filtering baseret på metafelter + sortering
    let filtered = applyFilterSet(matchedProducts, activeFilters);

    switch (activeSort) {
      case 'price-asc':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-desc':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'title-asc':
        filtered.sort((a, b) => a.title.localeCompare(b.title, 'da'));
        break;
      case 'title-desc':
        filtered.sort((a, b) => b.title.localeCompare(a.title, 'da'));
        break;
      default:
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
    }

    resultsCount.textContent = `${filtered.length} dæk fundet`;
    visibleCount = PER_PAGE;
    renderProductCards(filtered);
  }

  function renderProductCards(products) {
    filteredCache = products;
    resultsGrid.innerHTML = '';

    if (products.length === 0) {
      resultsGrid.innerHTML = '<li class="tf-no-results"><p>Ingen dæk matcher dine filtre.</p></li>';
      if (loadMoreBtn) loadMoreBtn.hidden = true;
      return;
    }

    const toShow = products.slice(0, visibleCount);
    if (loadMoreBtn) {
      loadMoreBtn.hidden = visibleCount >= products.length;
      loadMoreBtn.textContent = `Vis flere (${Math.min(PER_PAGE, products.length - visibleCount)} mere)`;
    }

    toShow.forEach((p) => {
      const priceFormatted = formatPrice(p.price, p.currency);
      const imgBase = p.image || '';
      const sep = imgBase.includes('?') ? '&' : '?';
      const li = document.createElement('li');
      li.className = 'grid__item';
      li.innerHTML = `
        <div class="card-wrapper product-card-wrapper underline-links-hover product-card"
          data-daek-bredde="${p.width}"
          data-daek-profil="${p.profile}"
          data-daek-tommer="${p.diameter}">
          <div class="card card--card card--media color-scheme-2 gradient" style="--ratio-percent: 100%;">
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              ${imgBase ? `
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <img
                    srcset="${imgBase}${sep}width=165 165w,${imgBase}${sep}width=360 360w,${imgBase}${sep}width=533 533w,${imgBase}${sep}width=720 720w,${imgBase}${sep}width=940 940w,${imgBase}${sep}width=1066 1066w"
                    src="${imgBase}${sep}width=533"
                    sizes="(min-width: 1200px) 267px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                    alt="${escapeHTML(p.imageAlt)}"
                    class="motion-reduce"
                    loading="lazy"
                    width="1080"
                    height="1080"
                  >
                </div>
              </div>
              ` : ''}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="/products/${p.handle}" class="full-unstyled-link">${escapeHTML(p.title)}</a>
                  </h3>
                </div>
                <div class="card__badge bottom left">
                  ${!p.available ? '<span class="badge badge--bottom-left color-3">Udsolgt</span>' : ''}
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="/products/${p.handle}" class="full-unstyled-link">${escapeHTML(p.title)}</a>
                </h3>
                <div class="card-information">
                  <div style="margin-top: 0.5rem;" class="caption-with-letter-spacing light">
                    Størrelse: ${p.width}/${p.profile}R${p.diameter}<br>
                    ${p.sason ? 'Sæson: ' + escapeHTML(p.sason) + '<br>' : ''}
                    ${p.fabrikant ? 'Mærke: ' + escapeHTML(p.fabrikant) + '<br>' : ''}
                    ${p.load && p.hastighed ? 'Load/Speed: ' + escapeHTML(p.load + p.hastighed) : ''}
                  </div>
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">Normalpris</span>
                        <span class="price-item price-item--regular">${priceFormatted}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      resultsGrid.appendChild(li);
    });
  }

  /* ─── URL management ─── */
  function updateURL() {
    const params = new URLSearchParams();
    if (selectedMake) params.set('make', selectedMake);
    if (selectedModel) params.set('model', selectedModel);
    if (selectedYear) params.set('year', selectedYear);
    const newURL = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    history.replaceState(null, '', newURL);
  }

  let pendingSeasonFilter = null;
  let rimFilter = null; // { diameter, width } fra konfigurator-URL

  // ETRTO: fælgbredde → [min, max] dækbredde
  const ETRTO = {
    '5.5':[155,185],'6.0':[175,195],'6.5':[185,205],'7.0':[195,215],
    '7.5':[205,225],'8.0':[215,235],'8.5':[225,245],'9.0':[235,255],
    '9.5':[245,265],'10.0':[255,275],'10.5':[265,285],'11.0':[275,295]
  };

  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    const make = params.get('make');
    const model = params.get('model');
    const year = params.get('year');
    const season = params.get('season');
    const rimDiameter = params.get('rim_diameter');
    const rimWidth = params.get('rim_width');
    if (!make) return;

    pendingSeasonFilter = season || null;
    if (rimDiameter) {
      rimFilter = {
        diameter: parseInt(rimDiameter, 10),
        width: rimWidth || null,
      };
    }

    const wait = setInterval(() => {
      if (makeEl.options.length > 1) {
        clearInterval(wait);
        makeEl.value = make;
        selectedMake = make;
        if (model) {
          populateModels(make).then(() => {
            modelEl.value = model;
            selectedModel = model;
            if (year) {
              populateYears(make, model).then(() => {
                yearEl.value = year;
                selectedYear = year;
                doSearch(make, model, year);
              });
            }
          });
        }
      }
    }, 100);
    setTimeout(() => clearInterval(wait), 10000);
  }

  /* ─── Helpers ─── */
  function showLoading(show) {
    if (loadingEl) {
      loadingEl.hidden = !show;
      loadingEl.style.display = show ? '' : 'none';
    }
  }

  function formatPrice(amount, currency) {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency || 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  }

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* ─── Init ─── */
  populateMakes().then(() => restoreFromURL());
})();
